import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Price from './Price';
import './ListingCard.css';

const TONES = {
  neutral: ['#3a3a3a', '#1a1a1a'], red: ['#4a1818', '#1a0808'],
  yellow:  ['#3a3012', '#1a1408'], silver: ['#4a4a52', '#1a1a20'],
  white:   ['#d8d8d8', '#9a9a9a'], teal:   ['#0e4a42', '#051a18'],
  sky:     ['#5a7a92', '#2a4050'],
};

function CarTile({ tone = 'neutral' }) {
  const [a, b] = TONES[tone] || TONES.neutral;
  return (
    <div style={{
      background: `linear-gradient(165deg, ${a} 0%, ${b} 100%)`,
      position: 'relative', overflow: 'hidden', width: '100%', height: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,.4)', padding: '3px 8px', border: '1px solid rgba(255,255,255,.15)', borderRadius: 2 }}>vehicle</span>
    </div>
  );
}

// Fondo que calcula el backend para cada foto (lib/imageProcessor.js →
// edgeBackground): '#rrggbb', o 'v:#arriba:#abajo' / 'h:#izq:#der' cuando los
// bordes difieren — cada mitad del relleno toma el color de su lado. Rellena
// el espacio que deja la foto al mostrarse completa en la tarjeta 9:16.
const HEX = '#[0-9a-f]{6}';
const SOLID_RE = new RegExp(`^${HEX}$`, 'i');
const SPLIT_RE = new RegExp(`^([vh]):(${HEX}):(${HEX})$`, 'i');

function imageBackground(bg) {
  if (!bg) return undefined;
  if (SOLID_RE.test(bg)) return bg;
  const m = SPLIT_RE.exec(bg);
  if (!m) return undefined;
  const dir = m[1] === 'v' ? 'to bottom' : 'to right';
  return `linear-gradient(${dir}, ${m[2]} 0 50%, ${m[3]} 50% 100%)`;
}

const ACTION_CLASS = {
  Venta:   'lcard__badge--venta',
  Cambio:  'lcard__badge--cambio',
  Mostrar: 'lcard__badge--mostrar',
};

export default function ListingCard({ listing, onClick, to }) {
  const { t } = useTranslation();
  const { _id, images, imageBg, imageBgs, name, brand, scale, price, locked, action, listing: linkedListing } = listing;
  const hasImg = images?.length > 0;
  // El feed del Home manda `imageBg`; las publicaciones del perfil llegan
  // completas, con `imageBgs` paralelo a `images`.
  const imgBackground = hasImg ? imageBackground(imageBg ?? imageBgs?.[0]) : undefined;
  // El backend puede enviar price como número (legacy) u objeto { amount, currency }.
  const priceAmount = price && typeof price === 'object' ? Number(price.amount) : Number(price);
  const priceCurrency = price && typeof price === 'object' ? (price.currency || 'USD') : 'USD';
  const showPrice = !locked && Number.isFinite(priceAmount) && priceAmount > 0;
  const badgeClass = ACTION_CLASS[action] || 'lcard__badge--mostrar';
  const badgeText = action ? t(`listing_card.action.${action}`, { defaultValue: action.toUpperCase() }) : '';

  // Si es una publicación enlazada al catálogo, navega al catálogo.
  // Si no, usa el id propio.
  const target = to || `/listing/${linkedListing?._id || linkedListing || _id}`;

  const inner = (
    <div className="lcard" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="lcard__img" style={imgBackground ? { background: imgBackground } : undefined}>
        {hasImg ? <img src={images[0]} alt={name} loading="lazy" /> : <CarTile tone="neutral" />}
        {action && <span className={`lcard__badge ${badgeClass}`}>{badgeText}</span>}
      </div>
      <div className="lcard__body">
        <p className="lcard__name" title={name}>{name}</p>
        <p className="lcard__meta">{[brand, scale].filter(Boolean).join(' · ') || '—'}</p>
        {showPrice ? (
          <Price className="lcard__price" amount={priceAmount} currency={priceCurrency} />
        ) : (
          <p className="lcard__price lcard__price--locked" title={t('listing_card.subscribe_tooltip')}>🔒 {t('listing_card.subscribe')}</p>
        )}
      </div>
    </div>
  );

  if (onClick) return inner;
  return (
    <Link to={target} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      {inner}
    </Link>
  );
}
